import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useTranslation } from 'react-i18next';
import FailedStatusIcon from '@/shared/assets/failed-status-icon.svg?react';

export type GetNewAddressStep = 'confirm' | 'success' | 'same' | 'error';

interface GetNewAddressDialogProps {
  step: GetNewAddressStep | null;
  isPending: boolean;
  errorMessage?: string;
  onGenerate: () => void;
  onClose: () => void;
}

export function GetNewAddressDialog({
  step,
  isPending,
  errorMessage,
  onGenerate,
  onClose,
}: GetNewAddressDialogProps) {
  const { t } = useTranslation();

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPending) {
      onClose();
    }
  };

  return (
    <Dialog open={!!step} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[38.4rem] py-[2.7rem] sm:py-[3.4rem] px-[3.2rem] gap-[3.2rem] rounded-[2.4rem] border-0 shadow-lg"
        showCloseButton={!isPending}>
        {(step === 'confirm' || step === 'same') && (
          <>
            <DialogHeader className="flex flex-row justify-between">
              <DialogTitle className="headline1 text-foreground text-left">
                {t('depositMethods.crypto.newAddress.title')}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-[3.2rem]">
              <p className="p3 text-foreground">
                {t(
                  step === 'confirm'
                    ? 'depositMethods.crypto.newAddress.description'
                    : 'depositMethods.crypto.newAddress.sameAddressDescription'
                )}
              </p>

              <div className="flex flex-col gap-[1.6rem]">
                {step === 'confirm' && (
                  <Button
                    onClick={onGenerate}
                    disabled={isPending}
                    loading={isPending}
                    className="w-full h-[5.6rem] rounded-[1.2rem] headline4">
                    {t('depositMethods.crypto.newAddress.generateButton')}
                  </Button>
                )}
                <Button
                  onClick={onClose}
                  variant="secondary"
                  disabled={isPending}
                  className="w-full h-[5.6rem] rounded-[1.2rem] headline4">
                  {t('common.close')}
                </Button>
              </div>
            </div>
          </>
        )}

        {(step === 'success' || step === 'error') && (
          <>
            <VisuallyHidden>
              <DialogTitle>
                {step === 'success'
                  ? t('depositMethods.crypto.newAddress.successTitle')
                  : t('depositMethods.crypto.newAddress.errorTitle')}
              </DialogTitle>
            </VisuallyHidden>

            <div className="flex flex-col gap-[3.2rem]">
              <div className="flex flex-col items-center gap-[2.4rem] py-[1.6rem]">
                {step === 'success' ? (
                  <img
                    src="/assets/success-status-icon.svg"
                    alt=""
                    className="size-26"
                  />
                ) : (
                  <FailedStatusIcon className="size-26 text-foreground" />
                )}
                <div className="flex flex-col items-center gap-[0.8rem]">
                  <span className="p2 text-center text-foreground">
                    {step === 'success'
                      ? t('depositMethods.crypto.newAddress.successTitle')
                      : t('depositMethods.crypto.newAddress.errorTitle')}
                  </span>
                  {step === 'error' && errorMessage && (
                    <span className="p3 text-center text-foreground">
                      {errorMessage}
                    </span>
                  )}
                </div>
              </div>

              <Button
                onClick={onClose}
                variant="secondary"
                className="w-full h-[5.6rem] rounded-[1.2rem] headline4">
                {t('common.close')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
